import { useEffect, useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, ZONA_HORARIA, formatMoney, hoy } from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { descargar, puedeCompartirArchivos } from '../lib/archivos.js';
import { etiquetaCortaLista, ordenarListas } from '../lib/catalogo.js';
import { catalogoEnPdf, gruposDelCatalogo, gruposSinPrecios } from '../lib/catalogoDePrecios.js';
import { nombreDeLleva } from '../lib/ofertas.js';
import { jpegDeFoto } from '../lib/jpegDeFoto.js';
import type { Jpeg } from '../lib/pdf.js';
import { localeActual, useT } from '../i18n/index.js';
import { etiquetaCategoriaProducto, etiquetaPresentacion } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { fechaCorta } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * «Catálogo de precios», the sheet «Productos» opens (the owner, 2026-10-19: «a catalog of prices
 * both wholesale and retail as a pdf that you can download»): the lists that go in, how many
 * products and pages that makes, and the PDF, sent from a phone or downloaded on a computer as
 * «Exportar»'s files are. Every list on sale is in to begin with, retail and wholesale side by
 * side; one taken out takes its column with it. In the app's language, as the rest of it is.
 *
 * Each product with its photo (the owner, 2026-10-22: «the price catalog function should use the
 * pictures we have saved obviously»), made ready as the sheet opens, from what this device holds,
 * so that the PDF is there the moment it is asked for: a phone lets a page share a file only right
 * after a tap.
 *
 * The offers running today on the lists chosen go first, «Ofertas», each with the price before it
 * and its last day (2026-11-03); the sheet says how many.
 *
 * «Con precios | Sin precios» on top (the owner, 2026-11-04: «the option to download the catalog pdf
 * WITHOUT any prices also just the product and their different packagings»): without, the sheet is
 * «Catálogo de productos», every product on sale with all its presentations, no lists to choose and
 * no offers, and so are the PDF and its file.
 */
export function CatalogoDePrecios({ onCerrar }: { onCerrar: () => void }) {
  const t = useT();
  const nombre = useNombreProducto();
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const ofertas = useLiveQuery(() => db.ofertas.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** With prices, the lists' columns; without, the products and their presentations alone. */
  const [conPrecios, setConPrecios] = useState(true);
  /** The lists taken out, so that one made while the sheet is open comes in as the others did. */
  const [fuera, setFuera] = useState<ReadonlySet<string>>(() => new Set());
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mandar = useMemo(() => puedeCompartirArchivos('application/pdf', 'prueba.pdf'), []);

  const activas = useMemo(() => ordenarListas(listas), [listas]);
  const elegidas = useMemo(() => activas.filter((l) => !fuera.has(l.id)), [activas, fuera]);
  const locale = localeActual();
  const grupos = useMemo(() => {
    const nombres = { producto: nombre, categoria: etiquetaCategoriaProducto, presentacion: etiquetaPresentacion, locale };
    return conPrecios
      ? gruposDelCatalogo(productos, unidades, precios, elegidas, nombres, {
          ofertas,
          hoy: hoy(),
          titulo: t('catalogo.ofertas'),
          nota: (antes, hasta) =>
            antes !== null
              ? t('catalogo.ofertaAntes', { antes: formatMoney(antes), hasta: fechaCorta(hasta) })
              : t('catalogo.ofertaHasta', { hasta: fechaCorta(hasta) }),
          notaLleva: (lleva, paga, porTodas, hasta) =>
            t('catalogo.ofertaLleva', { nombre: nombreDeLleva({ cantidad: lleva, paga }), lleva, monto: formatMoney(porTodas), hasta: fechaCorta(hasta) }),
        })
      : gruposSinPrecios(productos, unidades, nombres);
  }, [conPrecios, productos, unidades, precios, elegidas, nombre, locale, ofertas, t]);
  /** What it is called, on the sheet, in the PDF and in its file's name. */
  const titulo = t(conPrecios ? 'catalogo.titulo' : 'catalogo.tituloSinPrecios');
  /** The products the offers' group opens with, when there is one. */
  const enOferta = useMemo(() => (grupos[0]?.filas.some((f) => f.nota) ? new Set(grupos[0].filas.map((f) => f.productoId)).size : 0), [grupos]);
  const cuantos = useMemo(() => new Set(grupos.flatMap((g) => g.filas.map((f) => f.productoId))).size, [grupos]);
  /** The photos the catalogue draws, each once, as one key that changes only when they do. */
  const deLasFotos = useMemo(
    () => [...new Set(grupos.flatMap((g) => g.filas.flatMap((f) => (f.foto ? [f.foto] : []))))].sort().join(','),
    [grupos],
  );
  /** The photos made ready, and for which; a few at a time, the sheet saying how far along. */
  const [fotos, setFotos] = useState<{ de: string; listas: ReadonlyMap<string, Jpeg> } | null>(null);
  const [preparadas, setPreparadas] = useState(0);
  useEffect(() => {
    let vigente = true;
    const cola = deLasFotos ? deLasFotos.split(',') : [];
    const listas = new Map<string, Jpeg>();
    let hechas = 0;
    setPreparadas(0);
    void Promise.all(
      Array.from({ length: 4 }, async () => {
        for (let huella = cola.shift(); huella !== undefined; huella = cola.shift()) {
          const jpeg = await jpegDeFoto(huella);
          if (jpeg) listas.set(huella, jpeg);
          if (vigente) setPreparadas(++hechas);
        }
      }),
    ).then(() => {
      if (vigente) setFotos({ de: deLasFotos, listas });
    });
    return () => {
      vigente = false;
    };
  }, [deLasFotos]);
  const preparando = fotos?.de !== deLasFotos;
  const totalFotos = deLasFotos ? deLasFotos.split(',').length : 0;
  /** The products the PDF draws with a photo: those whose photo this device could make ready. */
  const conFoto = useMemo(
    () => new Set(grupos.flatMap((g) => g.filas.filter((f) => f.foto && fotos?.listas.has(f.foto)).map((f) => f.productoId))).size,
    [grupos, fotos],
  );
  const fecha = new Date().toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONA_HORARIA });
  // Laid out as soon as the lists are chosen, so the sheet can say how many pages it comes to.
  const documento = useMemo(
    () =>
      catalogoEnPdf(
        grupos,
        {
          negocio: config.negocioNombre,
          contacto: [config.negocioDireccion, config.negocioTelefono]
            .map((s) => s.trim())
            .filter(Boolean)
            .join(' · '),
          titulo,
          fecha,
          // Without prices, no lists: no price columns.
          listas: conPrecios ? elegidas.map(etiquetaCortaLista) : [],
          continua: (categoria) => t('catalogo.continua', { categoria }),
          pagina: (n, de) => t('catalogo.pagina', { n, de }),
        },
        fotos?.listas,
      ),
    [grupos, config, t, titulo, fecha, conPrecios, elegidas, fotos],
  );
  const listo = cuantos > 0 && !preparando;

  function cambiar(listaId: string, dentro: boolean) {
    setFuera((antes) => {
      const despues = new Set(antes);
      if (dentro) despues.delete(listaId);
      else despues.add(listaId);
      return despues;
    });
  }

  async function entregar(como: 'mandar' | 'descargar') {
    setOcupado(true);
    setError(null);
    try {
      const deQuien = t(conPrecios ? 'catalogo.tituloDe' : 'catalogo.tituloSinPreciosDe', { negocio: config.negocioNombre });
      const nombreArchivo = t(conPrecios ? 'catalogo.archivo' : 'catalogo.archivoSinPrecios', { fecha: hoy() });
      const archivo = new File([documento.bytes(deQuien)], nombreArchivo, { type: 'application/pdf' });
      if (como === 'mandar') await navigator.share({ files: [archivo], title: deQuien });
      else descargar(archivo);
    } catch (e) {
      // Closing the share sheet is a choice, not a failure.
      if (!(e instanceof DOMException && e.name === 'AbortError')) setError(t('catalogo.error'));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {mandar && (
            <button
              type="button"
              className="btn-primario w-full"
              disabled={!listo || ocupado}
              onClick={() => void entregar('mandar')}
            >
              <Ico icono="compartir" />
              {t('catalogo.mandar')}
            </button>
          )}
          <button
            type="button"
            className={`${mandar ? 'btn-secundario' : 'btn-primario'} w-full`}
            disabled={!listo || ocupado}
            onClick={() => void entregar('descargar')}
            data-catalogo-descargar
          >
            <Ico icono="descargar" />
            {ocupado ? t('exportar.preparando') : t('catalogo.descargar')}
          </button>
        </div>
      }
    >
      {/* With prices, or the products and their presentations alone (2026-11-04). */}
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('catalogo.precios')} data-catalogo-con-precios>
        {([true, false] as const).map((con) => (
          <button
            key={String(con)}
            type="button"
            aria-pressed={conPrecios === con}
            onClick={() => setConPrecios(con)}
            data-sin-icono="pestana"
            data-catalogo-precios={con ? 'con' : 'sin'}
            className={`min-h-10 flex-1 rounded-lg px-2.5 py-1 text-sm leading-tight transition ${
              conPrecios === con ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
            }`}
          >
            {t(con ? 'catalogo.conPrecios' : 'catalogo.sinPrecios')}
          </button>
        ))}
      </div>
      <p className="mb-4 text-sm text-slate-400" data-catalogo-explicacion>
        {t(conPrecios ? 'catalogo.explicacion' : 'catalogo.explicacionSinPrecios')}
      </p>
      {conPrecios && (
        <>
          <p className="mb-2 text-sm font-medium text-slate-300">{t('catalogo.listas')}</p>
          <div role="group" aria-label={t('catalogo.listas')} className="mb-4 flex flex-wrap gap-2" data-catalogo-listas>
            {activas.map((l) => {
              const dentro = !fuera.has(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  aria-pressed={dentro}
                  onClick={() => cambiar(l.id, !dentro)}
                  data-sin-icono="pestana"
                  data-lista={l.id}
                  className={`rounded-lg px-3 py-1.5 text-sm transition ${
                    dentro ? 'bg-marca-600 text-white hover:bg-marca-500' : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-slate-100'
                  }`}
                >
                  {etiquetaCortaLista(l)}
                </button>
              );
            })}
          </div>
        </>
      )}
      <p role="status" className="tabular text-sm text-slate-300" data-catalogo-cuantos>
        {conPrecios && elegidas.length === 0
          ? t('catalogo.eligeUna')
          : cuantos === 0
            ? t(conPrecios ? 'catalogo.ninguno' : 'catalogo.ningunoSinPrecios')
            : preparando
              ? t('catalogo.preparandoFotos', { n: preparadas, de: totalFotos })
              : t('catalogo.cuantos', { n: cuantos, paginas: documento.cuantasPaginas })}
      </p>
      {listo && conFoto > 0 && (
        <p className="tabular mt-1 text-xs text-slate-400" data-catalogo-fotos>
          {t('catalogo.conFotos', { n: conFoto })}
        </p>
      )}
      {listo && enOferta > 0 && (
        <p className="tabular mt-1 text-xs text-teal-300" data-catalogo-ofertas>
          {t('catalogo.conOfertas', { n: enOferta })}
        </p>
      )}
      {error && <p className="mt-3 rounded-xl bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>}
    </Modal>
  );
}
